import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { register } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { ArrowRight, Check, Gift, Loader2, Mail, Shield, Brain, FileText } from 'lucide-react';

export default function RegisterPage() {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [tosAccepted, setTosAccepted] = useState(false);
  const [newsletterOptIn, setNewsletterOptIn] = useState(false);
  const [loading, setLoading] = useState(false);
  const { loginUser } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!tosAccepted) {
      toast.error('You must accept the Terms of Service to continue');
      return;
    }
    
    setLoading(true);

    try {
      const res = await register({ 
        name, 
        email, 
        password, 
        tos_accepted: tosAccepted,
        newsletter_opt_in: newsletterOptIn
      });
      loginUser(res.data.access_token, res.data.user);
      
      // With 7-day trial, users go directly to product hub
      const trialDays = res.data.trial_days_remaining || 7;
      toast.success(`Welcome! Your ${trialDays}-day free trial has started.`);
      navigate('/hub');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  const features = [
    'Upload thousands of documents',
    'AI-powered exhibit identification',
    'Generate court-ready reports',
    'Search across all documents',
  ];

  return (
    <div className="min-h-screen flex" data-testid="register-page">
      {/* Left side - Branding */}
      <div className="hidden lg:flex flex-1 relative bg-navy overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-navy via-navy to-navy-light" />
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-10 w-32 h-32 border border-white/20 rounded-full" />
          <div className="absolute top-40 right-20 w-48 h-48 border border-white/20 rounded-full" />
          <div className="absolute bottom-32 left-32 w-24 h-24 border border-white/20 rounded-full" />
        </div>
        
        <div className="relative z-10 flex flex-col justify-between p-12 text-white h-full">
          {/* Logo */}
          <div className="flex items-center gap-3">
            <img src="/logo.png" alt="Legal AIDynamics" className="w-12 h-12 rounded-lg object-contain" />
            <div>
              <span className="font-heading text-2xl font-bold text-white">Legal AI</span>
              <span className="font-heading text-2xl font-bold text-bronze">Dynamics</span>
            </div>
          </div>

          {/* Features */}
          <div className="space-y-8">
            <h2 className="font-heading text-3xl font-bold leading-tight">
              Transform Your<br />Trial Preparation
            </h2>
            
            <div className="space-y-4">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-lg bg-bronze/20 flex items-center justify-center flex-shrink-0">
                  <FileText className="w-5 h-5 text-bronze" />
                </div>
                <div>
                  <h3 className="font-semibold">Legal AIDynamics</h3>
                  <p className="text-white/70 text-sm">AI-powered exhibit identification for trial prep</p>
                </div>
              </div>
              
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-lg bg-white/10 flex items-center justify-center flex-shrink-0">
                  <Brain className="w-5 h-5 text-white/70" />
                </div>
                <div>
                  <h3 className="font-semibold text-white/70">More Tools Coming</h3>
                  <p className="text-white/50 text-sm">Examination.AI, Intake.AI, Workflow.AI & more</p>
                </div>
              </div>
              
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-lg bg-white/10 flex items-center justify-center flex-shrink-0">
                  <Shield className="w-5 h-5 text-white/70" />
                </div>
                <div>
                  <h3 className="font-semibold text-white/70">Enterprise Security</h3>
                  <p className="text-white/50 text-sm">AES-256 encryption & compliance ready</p>
                </div>
              </div>
            </div>
          </div>

          {/* Trial Info */}
          <div className="flex items-center gap-3 p-4 rounded-lg bg-white/5 backdrop-blur">
            <Gift className="w-8 h-8 text-bronze" />
            <div>
              <p className="font-semibold">7-Day Free Trial</p>
              <p className="text-white/70 text-sm">No credit card required to start</p>
            </div>
          </div>
        </div>
      </div>

      {/* Right side - Form */}
      <div className="flex-1 flex items-center justify-center p-8 bg-gradient-to-b from-slate-50 to-white">
        <div className="w-full max-w-md animate-fade-in">
          {/* Mobile Logo */}
          <div className="lg:hidden flex items-center gap-3 mb-8 justify-center">
            <img src="/logo.png" alt="Legal AIDynamics" className="w-10 h-10 rounded-lg object-contain" />
            <div>
              <span className="font-heading text-xl font-bold text-navy">Legal AI</span>
              <span className="font-heading text-xl font-bold text-bronze">Dynamics</span>
            </div>
          </div>

          <Card className="border-border shadow-lg">
            <CardHeader className="space-y-1 text-center pb-2">
              <CardTitle className="font-heading text-2xl">Create Your Account</CardTitle>
              <CardDescription className="font-body">
                Start your free trial with Legal AIDynamics
              </CardDescription>
              {/* Trial info */}
              <div className="flex items-center justify-center gap-2 pt-2 text-sm bg-success/10 text-success rounded-md p-2 mt-2">
                <Gift className="w-4 h-4" />
                <span className="font-medium">7-day free trial • No credit card required</span>
              </div>
            </CardHeader>
            <CardContent className="pt-4">
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="name" className="font-body">Full Name</Label>
                  <Input
                    id="name"
                    type="text"
                    placeholder="Jane Doe"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    required
                    data-testid="register-name-input"
                    className="font-body"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email" className="font-body">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="attorney@lawfirm.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    data-testid="register-email-input"
                    className="font-body"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="password" className="font-body">Password</Label>
                  <Input
                    id="password"
                    type="password"
                    placeholder="Create a secure password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    minLength={6}
                    data-testid="register-password-input"
                    className="font-body"
                  />
                </div>

                {/* Terms of Service checkbox */}
                <div className="flex items-start space-x-2 pt-2">
                  <Checkbox 
                    id="tos" 
                    checked={tosAccepted}
                    onCheckedChange={setTosAccepted}
                    data-testid="tos-checkbox"
                  />
                  <label 
                    htmlFor="tos" 
                    className="text-sm font-body text-muted-foreground leading-tight cursor-pointer"
                  >
                    I agree to the{' '}
                    <Link 
                      to="/terms-of-service" 
                      className="text-bronze hover:underline"
                      target="_blank"
                      data-testid="tos-link"
                    >
                      Terms of Service
                    </Link>
                    {' '}and acknowledge the trial period terms
                  </label>
                </div>

                {/* Newsletter checkbox */}
                <div className="flex items-start space-x-2">
                  <Checkbox 
                    id="newsletter" 
                    checked={newsletterOptIn}
                    onCheckedChange={setNewsletterOptIn}
                    data-testid="newsletter-checkbox"
                  />
                  <label 
                    htmlFor="newsletter" 
                    className="text-sm font-body text-muted-foreground leading-tight cursor-pointer"
                  >
                    <Mail className="w-3 h-3 inline mr-1" />
                    Send me product updates and tips via email (optional)
                  </label>
                </div>

                <Button
                  type="submit"
                  className="w-full bg-navy hover:bg-navy-light font-body"
                  disabled={loading || !tosAccepted}
                  data-testid="register-submit-btn"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Creating account...
                    </>
                  ) : (
                    <>
                      Start Free Trial
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </>
                  )}
                </Button>
                <p className="text-xs text-center text-muted-foreground mt-2">
                  $20/month after trial • Cancel anytime
                </p>
              </form>
              <div className="mt-6 text-center text-sm font-body text-muted-foreground">
                Already have an account?{' '}
                <Link to="/login" className="text-bronze hover:underline" data-testid="login-link">
                  Sign in
                </Link>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
